import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Velkommen tilbake!");
      navigate("/dashboard");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <div className="px-6 lg:px-8 py-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-home">
          <ArrowLeft className="h-4 w-4" /> Tilbake
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6">
        <Card className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-sm">
          <CardContent className="p-10">
            <div className="flex items-center gap-2 mb-8">
              <div className="h-9 w-9 rounded-lg bg-slate-900 flex items-center justify-center text-white font-semibold">I</div>
              <span className="font-medium text-lg">IdeSetGo</span>
            </div>
            <h1 className="text-3xl font-medium tracking-tight text-slate-900">Logg inn</h1>
            <p className="mt-2 text-slate-500">Velkommen tilbake til IdeSetGo.</p>

            <Button
              type="button"
              variant="outline"
              onClick={googleLogin}
              className="mt-8 w-full h-11 rounded-xl border-gray-300"
              data-testid="google-login-btn"
            >
              <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Fortsett med Google
            </Button>

            <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
              <div className="flex-1 h-px bg-gray-200" />
              <span>eller med e-post</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">E-post</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="login-email" />
              </div>
              <div>
                <Label htmlFor="password">Passord</Label>
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="login-password" />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="login-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Logg inn"}
              </Button>
            </form>

            <p className="mt-8 text-sm text-slate-500 text-center">
              Ikke en konto enda? <Link to="/register" className="text-slate-900 underline underline-offset-4" data-testid="login-to-register">Registrer deg</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
